import { type ReactNode } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight, Plus } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageSeo from "@/components/site/PageSeo";

const SITE_URL = "https://www.cmopartner.fr";

export const TITLE = "Fractional CMO : direction marketing à temps partagé | CMO Partner";
export const DESCRIPTION =
  "Un Fractional CMO est un directeur marketing à temps partagé : stratégie, équipe, acquisition, 2 à 12 jours/mois. 16 ans d'expérience, ex-Decathlon, Lavazza.";

const pills = [
  "16 ans d'expérience internationale",
  "Ex-Decathlon · Lavazza · smsmode",
  "Disponible sous 2 semaines",
];

const scopeRows: { domain: string; examples: ReactNode }[] = [
  {
    domain: "Positionnement & messaging",
    examples: (
      <>Définir l'ICP, clarifier la proposition de valeur, construire le discours commercial et les arguments sales</>
    ),
  },
  {
    domain: "Go-to-market",
    examples: <>Choisir les canaux prioritaires, structurer le funnel, aligner marketing et sales</>,
  },
  {
    domain: "Acquisition & pipeline",
    examples: <>SEO/GEO, contenu, outbound, ABM, LinkedIn, paid, nurturing email</>,
  },
  {
    domain: "CRM & lifecycle",
    examples: (
      <>
        <Link to="/services/crm-lifecycle" className="text-foreground underline underline-offset-4 hover:text-primary">
          HubSpot
        </Link>
        , Salesforce — workflows, lead scoring, nurture sequences, automation
      </>
    ),
  },
  {
    domain: "IA & automatisation",
    examples: (
      <>
        <Link to="/services/ai-marketing" className="text-foreground underline underline-offset-4 hover:text-primary">
          Agents IA
        </Link>
        , automatisation de contenu, GEO optimization, workflows sans intervention manuelle
      </>
    ),
  },
  {
    domain: "Pricing & offre",
    examples: <>Restructuration tarifaire, segmentation, packaging, impact sur le panier moyen</>,
  },
  {
    domain: "Management & organisation",
    examples: <>Pilotage des équipes, recrutement, coordination agences et freelances, KPIs</>,
  },
];

const comparisonHead = ["Critère", "Fractional CMO", "CMO full-time", "Agence marketing", "Freelance spécialisé"];

const comparisonRows = [
  ["Tarification", "Sur devis", "80 à 150 k€ par an en coût total employeur", "Selon périmètre", "Selon périmètre"],
  ["Niveau de séniorité", "✓ Senior", "✓ Senior", "~ Variable", "~ Variable"],
  ["Délai pour démarrer", "1 à 2 semaines", "3 à 6 mois", "2 à 4 semaines", "1 à 2 semaines"],
  ["Responsabilité sur les résultats", "✓ Oui", "✓ Oui", "✗ Non", "✗ Non"],
  ["Vision stratégique", "✓ Oui", "✓ Oui", "✗ Non", "~ Limitée"],
  ["Pilotage de l'équipe interne", "✓ Oui", "✓ Oui", "✗ Non", "✗ Non"],
  ["Alignement avec vos objectifs", "✓ Fort", "✓ Fort", "✗ Faible", "~ Moyen"],
  ["Couverture B2B et B2C", "✓ Les deux", "~ À construire", "~ Généraliste", "~ Spécialisé"],
  ["Intégration IA & automatisation", "✓ Oui (builder)", "~ Variable", "~ Dépend de l'agence", "~ Variable"],
];

const fitCards = [
  {
    title: "Vous avez des actifs sous-exploités",
    body: "Contenu, audience, base clients, équipe commerciale — tout est là mais rien ne génère de pipeline prévisible. Il manque l'architecture qui relie tout.",
  },
  {
    title: "Vous n'avez pas encore de CMO interne",
    body: "Vous gérez le marketing vous-même entre deux réunions, ou vous avez un profil junior sans cap stratégique. Il manque quelqu'un qui prend les décisions et en répond.",
  },
  {
    title: "Votre budget ne justifie pas un CDI senior",
    body: "80 à 150 k€ par an en coût total employeur pour un CMO senior, plus 3 à 6 mois de recrutement. Le modèle Fractional propose des formats de 2 à 12 jours par mois, avec un tarif sur devis.",
  },
  {
    title: "Vous avez un déclencheur immédiat",
    body: "Levée de fonds à préparer, CMO qui vient de partir, nouveau marché à attaquer, budget à déployer intelligemment. Le Fractional démarre en moins de 2 semaines.",
  },
];

const differentiators = [
  {
    title: "B2B et B2C — les deux",
    body: "8 ans en B2B (SaaS, CPaaS, logiciel) + 8 ans en B2C (retail, consumer goods, e-commerce). La plupart des Fractional CMO maîtrisent l'un ou l'autre. Pas les deux à ce niveau de preuve.",
  },
  {
    title: "CMO qui construit",
    body: "HubSpot et Salesforce déployés from scratch. 3 SaaS lancés en autonomie (CosKitchen, PerfectCV, BatiPro). Je sais ce qu'il y a sous le capot d'un CRM, d'un tunnel de conversion, d'une stack marketing — pas seulement en théorie.",
  },
  {
    title: "International & multilingue",
    body: "Franco-américaine, 16 ans d'expérience sur les marchés France, UK, Italie, Espagne, Allemagne, USA, Colombie. Je pilote des équipes et des marchés à distance depuis toujours.",
  },
];

const proofBar = [
  "+50% leads inbound",
  "+25% ROI",
  "+30% croissance CA",
  "+1M€ nouveaux marchés",
  "10M€+ MRR supporté",
];

const missions = [
  {
    title: "Growth Architecture & CRM",
    intro:
      "Audit de vos actifs existants, architecture HubSpot ou Salesforce from scratch, séquences de nurture, lead scoring, automation marketing et sales. Le système convertit pendant que vous dormez.",
    items: [
      "Déploiement HubSpot complet (workflows, scoring, sequences, reporting)",
      "Restructuration CRM Salesforce pour une équipe commerciale de 5 à 20 personnes",
      "Connexion contenu existant → pipeline via nurturing automatisé",
      "Intégration Mixpanel / analytics pour mesure d'incrémentalité",
    ],
  },
  {
    title: "Stratégie Go-to-Market",
    intro:
      "Définition des canaux prioritaires, structuration du funnel, alignement marketing/sales, construction des offres et du pricing.",
    items: [
      "Lancement sur un nouveau marché (France, UK, DACH, Southern Europe)",
      "Restructuration du pricing et du packaging",
      "Définition ICP et segmentation",
      "Alignement marketing & sales (SLA, handoff, qualification, pipeline review)",
    ],
  },
  {
    title: "Acquisition & Pipeline B2B",
    intro:
      "Construction d'une machine d'acquisition durable : SEO/GEO, contenu, outbound, ABM, LinkedIn, paid. L'objectif n'est pas le trafic — c'est le pipeline qualifié et prévisible.",
    items: [
      "Stratégie SEO + GEO (optimisation pour les moteurs IA : Perplexity, ChatGPT, Google SGE)",
      "Stratégie outbound multi-touch (email, LinkedIn, téléphone)",
      "Campagnes ABM grands comptes",
      "Nurturing email par segment (PME / ETI / grands comptes)",
    ],
  },
  {
    title: "Stratégie Marketing B2C & E-commerce",
    intro:
      "Vision d'ensemble sur la stratégie omnicanale, pricing, influence, performance marketing, parcours client et expérience de marque.",
    items: [
      "Pilotage du replatforming e-commerce (Salesforce Commerce Cloud, Akeneo)",
      "Stratégie d'influence (micro/macro, mesure de conversion)",
      "Restructuration du pricing et de l'offre",
      "Performance marketing omnicanal (paid, SEO, email, marketplace)",
    ],
  },
  {
    title: "IA & Agentic Marketing",
    intro:
      "Identification des workflows manuels automatisables, construction des agents (Claude, n8n, Cowork), intégration dans l'organisation existante. L'objectif : gagner de la vélocité d'exécution, pas déployer de l'IA pour faire moderne.",
    items: [
      "Automatisation de la production de contenu (brief → draft → publish)",
      "Agents de scoring et qualification de leads",
      "Optimisation GEO (être cité par les LLMs)",
      "Automatisation des workflows HubSpot avec IA générative",
    ],
  },
  {
    title: "Management & Organisation Marketing",
    intro:
      "Recrutement des bons profils, pilotage des agences et freelances, structuration des process et des rituels, montée en compétence des équipes.",
    items: [
      "Recrutement d'un(e) Head of Marketing interne",
      "Pilotage d'agences SEO, paid, créa",
      "Mise en place des KPIs et dashboards",
      "Onboarding et formation équipe marketing",
    ],
  },
];

const steps = [
  {
    when: "Semaine 1-2",
    title: "Audit & diagnostic",
    body: "Je ne touche rien. Je comprends ce qui convertit déjà sans que vous le sachiez — et ce qui freine réellement. Entretiens équipe, analyse des actifs existants, lecture des données.",
  },
  {
    when: "Semaine 2-3",
    title: "Priorisation & plan d'action",
    body: "Pas un deck de 40 slides. Un document court qui dit : voici les 3 leviers prioritaires, voici l'ordre, voici ce qu'on mesure. Validé avec vous avant d'exécuter.",
  },
  {
    when: "Mois 1",
    title: "Première boucle",
    body: "Un segment prioritaire. Une séquence ou un canal. On mesure contre un groupe de contrôle. On ajuste. On ne passe pas à l'étape 2 sans signal positif.",
  },
  {
    when: "Mois 2-3",
    title: "Industrialisation",
    body: "Ce qui fonctionne passe en automation. Les workflows tournent sans intervention manuelle. L'équipe est formée pour piloter.",
  },
  {
    when: "M3+",
    title: "Transfert & autonomie",
    body: "Je construis pour que ça tourne après mon départ. Documentation, formation, recrutement si nécessaire. L'objectif n'est pas de créer de la dépendance.",
  },
];

const cases = [
  {
    name: "smsmode",
    tag: "B2B SaaS · CPaaS · COMEX",
    body: "Architecture CRM & lifecycle HubSpot from scratch. Équipe commerciale 10+ personnes, 4 marchés simultanés (FR, UK, DE, ES).",
    results: ["+50% leads inbound", "+25% ROI", "+14% CA", "+30% CTR"],
  },
  {
    name: "Lumivero",
    tag: "B2B SaaS · Direction commerciale",
    body: "Restructuration du pricing et du mix produit. Stratégie ABM + inbound. Management équipe de 10.",
    results: ["+30% croissance", "+1M€ nouveaux marchés", "+20% panier moyen"],
  },
  {
    name: "MaxiCoffee",
    tag: "E-commerce B2C · Replatforming SFCC",
    body: "Cadrage métier du replatforming Magento → Salesforce Commerce Cloud. 7 streams business, 2 go-lives internationaux.",
    results: ["Go-live IT/UK/EU 29 mai", "Go-live FR/DE 28 juillet", "7 streams livrés"],
  },
];

export const faq: { q: string; a: string; link?: { to: string; label: string } }[] = [
  {
    q: "Fractional CMO, directeur marketing à temps partagé ou externalisé : y a-t-il une différence ?",
    a: "Non, les trois expressions désignent le même rôle : une direction marketing senior qui intervient quelques jours par mois sans être salariée de l'entreprise. « Fractional CMO » est le terme anglo-saxon, « directeur marketing à temps partagé » décrit le rythme et « directeur marketing externalisé » le statut de prestataire extérieur.",
    link: { to: "/directeur-marketing-externalise", label: "Voir la page Directeur marketing externalisé →" },
  },
  {
    q: "Qu'est-ce qu'un Fractional CMO ?",
    a: "Un Fractional CMO est un directeur marketing senior qui intervient dans une entreprise à temps partiel, généralement 2 à 12 jours par mois. Contrairement à une agence qui exécute des tâches définies, il prend la responsabilité de la direction marketing : il définit la stratégie, choisit les canaux, pilote les équipes et rend des comptes sur des résultats business concrets (pipeline, CA, leads qualifiés). C'est un modèle particulièrement adapté aux PME, ETI et scale-ups qui ont besoin d'une expertise senior sans le coût ni le délai d'un recrutement CDI (80 à 150 k€ par an en coût total employeur). Le terme « Fractional » signifie que vous accédez à une fraction du temps d'un profil senior, partagé entre plusieurs entreprises.",
  },
  {
    q: "Combien coûte un Fractional CMO ?",
    a: "En France, une telle mission est facturée sur devis, selon le format retenu (léger 2 à 4 jours, standard 4 à 6 jours ou intensif 8 à 12 jours par mois) et la complexité des chantiers. Vous ne payez que les jours réellement nécessaires (de 2 à 12 par mois), sans charges sociales ni engagement long, alors qu'un CMO senior en CDI représente 80 à 150 k€ par an en coût total employeur. Pas de risque de mauvais match, sans préavis, et avec une mise en route en moins de 2 semaines. Le modèle offre une flexibilité que le recrutement ne permet pas : vous ajustez le volume selon vos besoins et votre trésorerie.",
  },
  {
    q: "Quelle différence entre un Fractional CMO et une agence marketing ?",
    a: "La différence est fondamentale et tient à la posture. Une agence exécute des tâches dans un scope défini et livre des livrables. Un Fractional CMO prend la responsabilité de votre direction marketing et optimise votre croissance. Concrètement, ce CMO à temps partagé définit la stratégie, choisit les canaux, pilote les prestataires et rend des comptes sur des résultats business. L'agence attend un brief, exécute, et facture à la livraison. Ce n'est pas le même rôle, ce n'est pas le même niveau d'engagement, et ce n'est pas le même impact sur votre pipeline.",
  },
  {
    q: "Combien de temps dure une mission Fractional CMO ?",
    a: "La plupart des missions durent entre 6 et 18 mois. Les 3 premiers mois servent à poser les fondations (audit, priorisation, premières boucles de croissance). À partir du mois 4-6, le système tourne et les résultats deviennent mesurables. L'engagement est flexible : vous pouvez ajuster ou arrêter selon l'évolution de vos besoins. L'objectif final est le transfert de compétences à votre équipe, pas la dépendance.",
  },
  {
    q: "Le Fractional CMO peut-il travailler à distance ?",
    a: "Oui, la grande majorité des missions se déroulent en remote avec des points hebdomadaires et des outils collaboratifs. J'ai piloté des équipes à distance en France, UK, Italie, Espagne, Allemagne et USA. Si vous êtes basé en France, je peux également intervenir en présentiel selon les besoins — notamment pour les sessions de stratégie ou les comités de direction.",
  },
  {
    q: "Fractional CMO ou Head of Marketing : quelle différence ?",
    a: "Un Head of Marketing est généralement un profil exécutant, focalisé sur l'opérationnel et la coordination. Un Fractional CMO intervient à un niveau décisionnel plus élevé : il définit les priorités, arbitre les budgets, et est responsable de la direction marketing dans son ensemble. Si vous avez déjà un Head of Marketing et cherchez quelqu'un qui fixe le cap et prend les décisions stratégiques, c'est une direction marketing à temps partagé qu'il vous faut. Les deux rôles peuvent coexister.",
  },
  {
    q: "CMO Partner intervient-il en B2B ou B2C ?",
    a: "Les deux — c'est une singularité rare à ce niveau de séniorité. J'ai 8 ans d'expérience en B2B (SaaS, CPaaS, logiciel) notamment chez smsmode et Lumivero, et 8 ans en B2C (retail, consumer goods, e-commerce) notamment chez Decathlon pendant près de 10 ans et Lavazza. Cette double expertise me permet d'intervenir sur des entreprises qui ont des enjeux mixtes B2B/B2C, ou de naviguer entre les deux selon les phases de croissance.",
  },
  {
    q: "Quand faut-il faire appel à un Fractional CMO ?",
    a: "Le modèle est pertinent dans 4 situations : (1) vous avez validé votre traction et devez structurer votre acquisition sans recruter un CMO full-time trop tôt ; (2) vous préparez une levée de fonds et avez besoin de solidifier votre go-to-market ; (3) votre CMO interne vient de partir et vous avez besoin d'assurer la continuité ; (4) vous avez un budget marketing à déployer mais pas la direction stratégique pour le faire intelligemment. En revanche, si vous cherchez principalement de l'exécution opérationnelle, une agence ou un freelance spécialisé sera plus adapté.",
  },
];

const SectionTitle = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <h2 className={`text-3xl font-bold leading-tight md:text-4xl ${className}`}>
    {children}
    <span className="text-primary">.</span>
  </h2>
);

export const FRACTIONAL_CMO_SCHEMA = [
      {
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Tiphany Rigaudias",
        jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"],
        url: `${SITE_URL}/a-propos`,
        sameAs: ["https://linkedin.com/in/tiphanyrigaudias"],
        knowsAbout: [
          "Marketing B2B",
          "Marketing automation",
          "IA appliquée au marketing",
          "Formation des équipes marketing",
          "Fractional CMO",
          "Growth Marketing",
          "CRM",
          "HubSpot",
          "Salesforce Commerce Cloud",
          "B2B SaaS Marketing",
          "E-commerce",
          "AI Marketing",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Fractional CMO",
        serviceType: "Direction marketing à temps partagé",
        description:
          "Direction marketing à temps partagé : stratégie, go-to-market, acquisition, CRM et automatisation, 2 à 12 jours par mois.",
        provider: {
          "@type": "Organization",
          name: "CMO Partner",
          url: SITE_URL,
          sameAs: ["https://linkedin.com/in/tiphanyrigaudias"],
        },
        areaServed: { "@type": "Country", name: "France" },
        availableLanguage: ["French", "English"],
      },
    ];

const FractionalCmo = () => {

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageSeo path="/fractional-cmo" title={TITLE} description={DESCRIPTION} />
      <SiteNav />

      <main>
        {/* SECTION 1 — HERO */}
        <section className="border-b border-border px-5 pb-16 pt-20 md:pb-20 md:pt-28">
          <div className="mx-auto max-w-5xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
              <Link to="/" className="transition-colors hover:text-primary">
                Accueil
              </Link>
              <span className="px-2">/</span>
              <span className="text-foreground">Fractional CMO</span>
            </nav>
            <h1 className="max-w-3xl text-4xl font-bold leading-[1.1] md:text-6xl">
              Fractional CMO — Direction marketing senior à temps partagé
              <span className="text-primary">.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-xl">
              Une CMO senior dans votre équipe, 2 à 12 jours par mois. Sans le coût ni le délai d'un recrutement
              full-time.
            </p>

            <div className="mt-14 grid gap-10 md:grid-cols-2 md:items-start">
              <ul className="flex flex-wrap gap-3">
                {pills.map((pill) => (
                  <li key={pill} className="rounded-lg bg-secondary px-4 py-2 text-sm font-medium">
                    {pill}
                  </li>
                ))}
              </ul>

              <div className="rounded-lg border border-border bg-secondary p-7">
                <p className="text-xl font-bold">Parlons de votre situation</p>
                <p className="mt-3 leading-relaxed text-muted-foreground">
                  30 minutes pour identifier vos leviers prioritaires.
                </p>
                <div className="mt-6">
                  <BookingButton label="Réserver un appel gratuit" location="fractional_cmo_hero" large />
                </div>
                <p className="mt-4 text-sm text-muted-foreground">Sans engagement · Réponse sous 24h</p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 — DEFINITION */}
        <section className="border-b border-border px-5 py-20 md:py-24">
          <div className="mx-auto max-w-4xl">
            <SectionTitle>Qu'est-ce qu'un Fractional CMO ?</SectionTitle>
            <div className="mt-8 space-y-5 text-lg leading-relaxed text-muted-foreground">
              <p>
                Un Fractional CMO — ou directeur marketing à temps partagé, aussi appelé{" "}
                <Link
                  to="/directeur-marketing-externalise"
                  className="text-foreground underline underline-offset-4 hover:text-primary"
                >
                  directeur marketing externalisé
                </Link>{" "}
                — est un directeur marketing senior qui intervient dans votre entreprise à temps partiel, généralement
                selon un format de 2 à 12 jours par mois. Il apporte le même niveau de responsabilité et d'expertise qu'un CMO interne,
                sans le coût ni les contraintes d'un CDI senior.
              </p>
              <p>
                Le terme « Fractional » vient de l'anglais : vous accédez à une fraction du temps d'un profil senior,
                partagé entre plusieurs entreprises. En France, on parle aussi de CMO à temps partagé, directeur
                marketing freelance ou CMO à temps partiel. C'est le modèle que je pratique avec mes clients depuis
                plusieurs années.
              </p>
            </div>

            <div className="mt-10 rounded-lg bg-secondary p-7 text-lg leading-relaxed">
              Son rôle n'est pas d'exécuter du marketing. Son objectif est de construire un système
              de croissance cohérent, mesurable et scalable — et de le faire tourner avec votre équipe.
            </div>

            <h3 className="mt-16 text-2xl font-bold">Ce qu'un Fractional CMO peut piloter</h3>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-foreground">
                    <th scope="col" className="w-1/3 py-4 pr-6 text-sm font-bold uppercase tracking-wide">
                      Domaine
                    </th>
                    <th scope="col" className="py-4 text-sm font-bold uppercase tracking-wide">
                      Exemples de missions concrets
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {scopeRows.map((row) => (
                    <tr key={row.domain} className="border-b border-border align-top">
                      <th scope="row" className="py-5 pr-6 font-semibold">
                        {row.domain}
                      </th>
                      <td className="py-5 leading-relaxed text-muted-foreground">{row.examples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* SECTION 3 — COMPARISON */}
        <section className="border-b border-border px-5 py-20 md:py-24">
          <div className="mx-auto max-w-5xl">
            <SectionTitle>Fractional CMO, CMO full-time, agence : comment choisir ?</SectionTitle>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">
              Quand une entreprise veut structurer son marketing, plusieurs options existent. Elles ne répondent pas aux
              mêmes besoins ni aux mêmes budgets. Voici une comparaison honnête.
            </p>

            <div className="mt-10 overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-foreground">
                    {comparisonHead.map((head, i) => (
                      <th
                        key={head}
                        scope="col"
                        className={`py-4 pr-6 font-bold uppercase tracking-wide ${i === 1 ? "text-primary" : ""}`}
                      >
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row[0]} className="border-b border-border align-top">
                      {row.map((cell, i) => (
                        <td
                          key={`${row[0]}-${i}`}
                          className={`py-4 pr-6 leading-relaxed ${
                            i === 0 ? "font-semibold text-foreground" : i === 1 ? "font-medium" : "text-muted-foreground"
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Tarifs indicatifs marché français 2026. CMO Partner intervient uniquement en mode Fractional — pas en
              agence, pas en régie.
            </p>

            <h3 className="mt-16 text-2xl font-bold">Fractional CMO vs agence : la différence fondamentale</h3>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <div className="rounded-lg border border-border p-7">
                <p className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Agence</p>
                <p className="mt-4 leading-relaxed text-muted-foreground">
                  Exécute dans un scope défini. Livre des campagnes, des contenus, des reports. Optimise son propre
                  contrat. N'est pas responsable du pipeline.
                </p>
              </div>
              <div className="rounded-lg border-2 border-primary bg-secondary p-7">
                <p className="text-sm font-bold uppercase tracking-wide text-primary">Fractional CMO</p>
                <p className="mt-4 leading-relaxed">
                  Prend la responsabilité de votre direction marketing. Définit la stratégie, choisit les canaux, pilote
                  les prestataires. Rend des comptes sur des métriques business : leads qualifiés, pipeline, CA
                  attribué.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 — POUR QUI */}
        <section className="border-b border-border px-5 py-20 md:py-24">
          <div className="mx-auto max-w-5xl">
            <SectionTitle>Un Fractional CMO est fait pour vous si…</SectionTitle>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {fitCards.map((card) => (
                <div key={card.title} className="rounded-lg border border-border p-7">
                  <h3 className="text-xl font-bold">{card.title}</h3>
                  <p className="mt-4 leading-relaxed text-muted-foreground">{card.body}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-20 text-3xl font-bold leading-tight md:text-4xl">
              Ce n'est probablement pas fait pour vous si…
              <span className="text-primary">.</span>
            </h2>
            <div className="mt-8 space-y-5 rounded-lg bg-secondary p-8 text-lg leading-relaxed text-muted-foreground">
              <p>Le modèle Fractional n'est pas la bonne réponse à toutes les situations. Soyons directs.</p>
              <p>
                → Si vous cherchez principalement de l'exécution opérationnelle (rédiger des posts, gérer des campagnes
                ads), un freelance spécialisé ou une agence sera plus adapté.
              </p>
              <p>
                → Si vous avez déjà une équipe marketing de 8+ personnes, vous avez besoin d'un CMO interne à temps
                plein.
              </p>
              <p>
                → Si vous êtes en pre product-market fit, le sujet n'est pas encore le marketing — c'est la validation
                produit et les premières ventes.
              </p>
              <p className="text-foreground">
                Un Fractional CMO apporte de la direction, de la structure et de la responsabilité sur les résultats.
                Pas des bras supplémentaires.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 5 — DIFFERENTIATION */}
        <section className="border-b border-border px-5 py-20 md:py-24">
          <div className="mx-auto max-w-5xl">
            <SectionTitle>Pourquoi CMO Partner plutôt qu'un autre Fractional CMO ?</SectionTitle>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground">
              La plupart des Fractional CMO ont fait du marketing ou du CRM ou du produit. Rarement les trois, et
              rarement quelqu'un qui a aussi construit ses propres produits SaaS de zéro.
            </p>

            <div className="mt-12 grid gap-8 md:grid-cols-3">
              {differentiators.map((diff, index) => (
                <div key={diff.title}>
                  <span className="text-sm font-bold text-primary">{String(index + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 text-xl font-bold">{diff.title}</h3>
                  <p className="mt-4 leading-relaxed text-muted-foreground">{diff.body}</p>
                  {diff.title === "CMO qui construit" && (
                    <Link to="/a-propos" className="mt-4 inline-block text-sm font-semibold text-primary hover:underline">Qui suis-je ?</Link>
                  )}
                </div>
              ))}
            </div>

            <ul className="mt-14 flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-border pt-8 text-sm font-medium">
              {proofBar.map((item, i) => (
                <li key={item} className="flex items-center gap-4">
                  {i > 0 && <span className="text-border" aria-hidden="true">·</span>}
                  <span className="text-primary">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* SECTION 6 — MISSIONS */}
        <section className="border-b border-border px-5 py-20 md:py-24">
          <div className="mx-auto max-w-4xl">
            <SectionTitle>Les missions concrètes d'un Fractional CMO</SectionTitle>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Le rôle ne se limite pas à établir une stratégie sur des slides. L'objectif est de construire un système
              de croissance adapté à votre situation réelle — puis de le faire tourner avec votre équipe.
            </p>

            <div className="mt-10 border-t border-border">
              {missions.map((mission) => (
                <details key={mission.title} className="group border-b border-border">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-left">
                    <h3 className="text-xl font-bold">{mission.title}</h3>
                    <Plus
                      className="h-5 w-5 shrink-0 text-primary transition-transform group-open:rotate-45"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="pb-8">
                    <p className="leading-relaxed text-muted-foreground">{mission.intro}</p>
                    <p className="mt-6 text-sm font-bold uppercase tracking-wide">Exemples de missions</p>
                    <ul className="mt-4 space-y-3">
                      {mission.items.map((item) => (
                        <li key={item} className="flex gap-3 leading-relaxed text-muted-foreground">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              ))}
            </div>

            <div className="mt-12 rounded-lg bg-secondary p-7">
              <p className="text-lg font-semibold">30 minutes pour identifier vos leviers prioritaires.</p>
              <div className="mt-5">
                <BookingButton label="Réserver un appel gratuit" location="fractional_cmo_mid" />
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7 — METHODE */}
        <section className="border-b border-border px-5 py-20 md:py-24">
          <div className="mx-auto max-w-4xl">
            <SectionTitle>Ma méthode : premières actions dès le premier mois, un système en place en 90 jours</SectionTitle>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Je ne passe pas 3 mois à construire une stratégie PowerPoint. J'audite, je priorise, je construis — et la
              première boucle tourne avant la fin du mois 1.
            </p>

            <ol className="mt-12 space-y-10 border-l border-border pl-8">
              {steps.map((step, index) => (
                <li key={step.title} className="relative">
                  <span
                    className="absolute -left-[41px] mt-2 h-3 w-3 rounded-full bg-primary"
                    aria-hidden="true"
                  />
                  <span className="text-sm font-bold text-primary">
                    {String(index + 1).padStart(2, "0")} · {step.when}
                  </span>
                  <h3 className="mt-2 text-xl font-bold">{step.title}</h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* SECTION 8 — RESULTATS */}
        <section className="border-b border-border px-5 py-20 md:py-24">
          <div className="mx-auto max-w-5xl">
            <SectionTitle>Résultats clients</SectionTitle>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              Des missions mesurables, pas des promesses.
            </p>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {cases.map((item) => (
                <article key={item.name} className="flex flex-col rounded-lg border border-border p-7">
                  <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{item.tag}</span>
                  <h3 className="mt-4 text-xl font-bold">{item.name}</h3>
                  <p className="mt-4 flex-1 leading-relaxed text-muted-foreground">{item.body}</p>
                  <ul className="mt-6 space-y-2 border-t border-border pt-5">
                    {item.results.map((result) => (
                      <li key={result} className="font-semibold text-primary">
                        {result}
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border px-5 py-16">
          <div className="mx-auto max-w-4xl rounded-lg bg-secondary p-7 md:p-10">
            <h2 className="text-2xl font-bold md:text-3xl">Votre équipe progresse pendant la mission<span className="text-primary">.</span></h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">Chaque action pilotée est l'occasion de transmettre la méthode : coaching du responsable marketing, ateliers et formation aux outils CRM et IA. L'objectif : une équipe capable de piloter son marketing en autonomie.</p>
            <Link to="/services/montee-en-competences-marketing" className="mt-5 inline-block font-semibold text-primary hover:underline">La montée en compétences →</Link>
          </div>
        </section>

        {/* SECTION 9 — FAQ */}
        <section className="border-b border-border bg-secondary px-5 py-20 md:py-24">
          <div className="mx-auto max-w-4xl">
            <SectionTitle>Questions fréquentes sur le Fractional CMO</SectionTitle>
            <div className="mt-10 border-t border-border">
              {faq.map((item) => (
                <details key={item.q} className="group border-b border-border">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-left">
                    <h3 className="text-lg font-bold">{item.q}</h3>
                    <Plus
                      className="h-5 w-5 shrink-0 text-primary transition-transform group-open:rotate-45"
                      aria-hidden="true"
                    />
                  </summary>
                  <p className="pb-8 leading-relaxed text-muted-foreground">
                    {item.a}
                    {item.link && <> <Link to={item.link.to} className="font-semibold text-primary hover:underline">{item.link.label}</Link></>}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* INTERNAL LINKS */}
        <section className="border-b border-border px-5 py-16">
          <div className="mx-auto max-w-5xl">
            <h2 className="text-xl font-bold">À lire aussi</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {[
                { to: "/fractional-cmo-b2b-saas", label: "Fractional CMO B2B SaaS" },
                { to: "/fractional-cmo-b2c-ecommerce", label: "Fractional CMO B2C & e-commerce" },
                { to: "/fractional-cmo-bordeaux", label: "Fractional CMO à Bordeaux" },
                { to: "/fractional-cmo-france", label: "Fractional CMO France : offre et fonctionnement" },
                { to: "/services/crm-lifecycle", label: "CRM & Lifecycle — HubSpot, Salesforce" },
                { to: "/services/ai-marketing", label: "IA & automatisation" },
                { to: "/directeur-marketing-externalise", label: "Directeur marketing externalisé" },
                { to: "/blog/fractional-cmo-definition-missions-tarifs", label: "Fractional CMO : tarifs, formats et missions" },
                { to: "/blog/cmo-externalise-vs-agence-vs-recrutement", label: "CMO externalisé, agence ou CDI" },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center justify-between gap-4 rounded-lg border border-border p-5 transition-colors hover:border-primary"
                >
                  <span className="font-semibold">{link.label}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                </Link>
              ))}
            </div>
            <p className="mt-8 text-sm text-muted-foreground">
              Basée à{" "}
              <Link to="/fractional-cmo-bordeaux" className="underline underline-offset-4 hover:text-primary">
                Bordeaux
              </Link>
              , disponible partout en France et à l'international.
            </p>
          </div>
        </section>

        {/* SECTION 10 — CTA FINAL */}
        <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
          <div className="mx-auto max-w-3xl">
            <h2 className="text-4xl font-bold leading-tight md:text-5xl">
              Prêt à transformer votre marketing en machine de croissance ?
              <span className="text-primary">.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-background/70">
              30 minutes pour comprendre votre situation, identifier vos leviers prioritaires et voir si le modèle
              Fractional est adapté à votre contexte.
            </p>
            <div className="mt-10">
              <BookingButton label="Réserver un appel gratuit" location="fractional_cmo_final" large /><WriteMeLink />
            </div>
            <p className="mt-6 text-sm text-background/50">
              Sans engagement · Réponse sous 24h · Disponible sous 2 semaines
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default FractionalCmo;
